<template>
  <v-app>
    <SectionLoader :status="isLoading" />
    <router-view v-if="canRenderRoute"></router-view>
    <notifications group="default" position="top right" animation-type="velocity" />
    <notifications group="gtm" position="top right" :duration="500" />
    <AppModal :data="data" />
    <div id="dialogs-wrapper"></div>
    <VideoDialog v-if="videoModal.isOpen" :data="videoModal" @close="hideVideoTutorial" />
  </v-app>
</template>

<script>
import { mapState } from "vuex";
import AppModal from "@/components/ui/AppModal.vue";
import SectionLoader from "@/components/Loader/SectionLoader.vue";
import VideoDialog from "@/components/ui/VideoDialog.vue";
import moment from "moment";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import { isFeatureFlagEnabled, FEATURE_FLAG } from "@/helpers/posthog";
import { getSubscriptionViewRedirect } from "@/helpers/subscription-view";

export default {
  name: "App",
  components: {
    AppModal,
    SectionLoader,
    VideoDialog
  },
  computed: {
    subscriptionViewContext() {
      const company = this.$store.state.company.company;
      return {
        enabled: this.$store.getters["settings/stripeSubscriptionUiEnabled"],
        hasSelfServiceBilling: this.$store.getters["company/hasSelfServiceBilling"],
        companyId: company && company.id
      };
    },
    canRenderRoute() {
      return (
        !this.$route.matched.some(record => record.meta.requiresAuth) ||
        this.$store.getters["auth/isAuthenticated"]
      );
    },
    ...mapState({
      data: state => state.settings.modalData,
      videoModal: state => state.settings.videoModal
    }),
    selectedLanguage() {
      return this.$store.state.settings.selectedLocale;
    },
    isLoading() {
      return this.$store.state.company.loading && this.$route.name !== "session-login";
    }
  },
  created() {
    document.documentElement.lang = this.$i18n.locale;
    document.documentElement.translate = false;
  },

  watch: {
    subscriptionViewContext({ enabled, companyId, hasSelfServiceBilling }) {
      if (!companyId || !this.$store.getters["auth/isAuthenticated"]) return;
      const redirect = getSubscriptionViewRedirect(this.$route, enabled, hasSelfServiceBilling);
      if (redirect) this.$router.replace(redirect);
    },
    async "$store.state.company.company"(current, last) {
      if (current && !current.parent) {
        await this.$store.dispatch("company/clearCompanyParent");
      }
      if (current && current.parent && (!last || current.parent !== last.parent)) {
        await this.$store.dispatch("company/setCompanyParent", current.id);
      }
    },
    $route: {
      handler() {
        const isFreeTool = this.$route.meta.isFreeTool;
        const isOpenedFromEmail = this.$route.query.source === "email";
        this.$gtm.trackCustomEvent({
          event: GTM_EVENT_NAME.PAGE_VIEW,
          page_url: this.$route.path,
          page_name: this.$route.name,
          ...(isFreeTool && { opened_from_mail: isOpenedFromEmail })
        });

        if (!this.$store.getters["auth/isAuthenticated"]) {
          return;
        }

        let is_paying = null;
        let is_testing = null;
        if (this.$store.state.company.company) {
          is_paying = this.$store.state.company.parent
            ? this.$store.state.company.parent.is_paying
            : this.$store.state.company.company.is_paying;

          is_testing = this.$store.state.company.parent
            ? this.$store.state.company.parent.is_testing
            : this.$store.state.company.company.is_testing;
        }
        const getSubscriptionStatus = () => {
          if (is_paying || (is_paying && is_testing)) {
            return "is_paying";
          } else if (!is_paying && is_testing) {
            return "is_testing";
          } else if (!is_paying && !is_testing) {
            return "other";
          }
        };
        this.$setUserProperties({
          login_status: "logged_in", // 'logged_in' | 'logged_out
          subscription_status: getSubscriptionStatus()
        });

        if (
          !isFeatureFlagEnabled(FEATURE_FLAG.CN_5810_INTERCOM_INTEGRATION) &&
          window.UE &&
          window.UE.pageHit &&
          this.$store.state.company
        ) {
          const userAuthEmail = this.$store.state.auth.user.email;
          const { user_id } = this.$store.state.company.company.company_user;
          const { id: company_id } = this.$store.state.company.company;

          window.UE.pageHit({
            ...window.civchat,
            user_id,
            company_id,
            email: userAuthEmail
          });
        }
      }
    },
    selectedLanguage: {
      handler(current) {
        this.$validator.locale = current;
        this.$i18n.locale = current;
        moment.locale(current);
      },
      immediate: true
    }
  },
  methods: {
    hideVideoTutorial() {
      this.$store.dispatch("settings/setVideoModal", {
        isOpen: false,
        videoUrl: null
      });
    }
  }
};
</script>
<style lang="scss">
@import "./assets/scss/new/styles.scss";
</style>

<style lang="scss" scoped>
html,
body {
  overflow-x: hidden !important;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  .v-application {
    font-family: $font-family-base !important;
    font-weight: $font-weight-medium;
    position: relative;
    width: 100%;
    min-height: 100%;
    overflow-x: hidden !important;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    overflow-wrap: break-word;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    -webkit-touch-callout: none;
    -webkit-text-size-adjust: 100%;
    -ms-text-size-adjust: 100%;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
}
</style>
